/**
 * Canvas-based handwriting renderer.
 * Renders text character-by-character with perturbations to simulate handwriting.
 */

import { calculateLayout, DEFAULT_LAYOUT } from './layout.js';
import { waitForFont } from './fonts.js';

/**
 * Default perturbation parameters (matching original Python handright defaults).
 */
export const DEFAULT_PERTURBATION = {
  lineSpacingSigma: 0,
  fontSizeSigma: 2,
  wordSpacingSigma: 2,
  perturbXSigma: 3,
  perturbYSigma: 3,
  perturbThetaSigma: 0.05,
  strikethroughProbability: 0.005,
  strikethroughLengthSigma: 2,
  strikethroughWidthSigma: 2,
  strikethroughAngleSigma: 2,
  strikethroughWidth: 8,
  inkDepthSigma: 30,
};

/**
 * Simple seeded pseudo-random number generator (mulberry32).
 * Allows reproducible "random" perturbations.
 */
class SeededRandom {
  constructor(seed) {
    this.seed = seed || Math.floor(Math.random() * 2147483647);
  }

  /** Returns a float in [0, 1) */
  next() {
    let t = (this.seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Returns a normally-distributed random number (Box-Muller) */
  gaussian(mean = 0, sigma = 1) {
    const u1 = this.next();
    const u2 = this.next();
    const z = Math.sqrt(-2 * Math.log(u1 || 0.0001)) * Math.cos(2 * Math.PI * u2);
    return mean + z * sigma;
  }
}

/**
 * Generate a notebook-style background with ruled lines.
 *
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {object} options - Layout/spacing options
 * @returns {HTMLCanvasElement}
 */
export function createNotebookBackground(width, height, options) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // White background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const {
    lineSpacing = 200,
    marginTop = 50,
    marginBottom = 50,
    marginLeft = 50,
    marginRight = 50,
    isUnderlined = true,
  } = options;

  if (isUnderlined) {
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;

    let y = marginTop + lineSpacing;
    while (y < height - marginBottom) {
      ctx.beginPath();
      ctx.moveTo(marginLeft, y);
      ctx.lineTo(width - marginRight, y);
      ctx.stroke();
      y += lineSpacing;
    }
  }

  return canvas;
}

/**
 * Render a single page of handwriting onto a canvas.
 *
 * @param {string} text - The full text to render
 * @param {object} template - Template options (layout + perturbation)
 * @param {number} pageIndex - Which page to render (0-based)
 * @param {object} [opts] - Additional options
 * @param {number} [opts.seed] - Random seed for reproducible output
 * @param {HTMLImageElement|HTMLCanvasElement} [opts.background] - Background image
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderPage(text, template, pageIndex, opts = {}) {
  const layoutOpts = { ...DEFAULT_LAYOUT, ...template };
  const perturbOpts = { ...DEFAULT_PERTURBATION, ...template };
  const seed = opts.seed || Math.floor(Math.random() * 2147483647) + pageIndex * 10000;
  const rng = new SeededRandom(seed);

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = layoutOpts.width;
  canvas.height = layoutOpts.height;
  const ctx = canvas.getContext('2d');

  // Draw background
  if (opts.background) {
    ctx.drawImage(opts.background, 0, 0, canvas.width, canvas.height);
  } else {
    const bgCanvas = createNotebookBackground(
      canvas.width,
      canvas.height,
      layoutOpts
    );
    ctx.drawImage(bgCanvas, 0, 0);
  }

  // Calculate layout using a temporary context for measurement
  // We use the same font string for measurement and rendering
  const baseFontSize = layoutOpts.fontSize;
  const fontFamily = template.fontFamily || 'sans-serif';

  // Wait for font to be ready
  if (fontFamily !== 'sans-serif' && fontFamily !== 'serif') {
    await waitForFont(fontFamily);
  }

  // Use a temporary canvas to measure text (matching render font)
  const measCanvas = document.createElement('canvas');
  const measCtx = measCanvas.getContext('2d');
  measCtx.font = `${baseFontSize}px "${fontFamily}"`;

  const pages = calculateLayout(text, layoutOpts, measCtx);

  // If the requested page doesn't exist, return blank canvas
  if (pageIndex >= pages.length) {
    return canvas;
  }

  const page = pages[pageIndex];

  // Ink base color
  const inkR = 0;
  const inkG = 0;
  const inkB = 0;

  // Render each character
  for (const line of page.lines) {
    for (const charData of line.chars) {
      const { char, x, y } = charData;

      // Skip spaces and control characters
      if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
        continue;
      }

      // Generate perturbations
      const fontSize = Math.max(1, baseFontSize + rng.gaussian(0, perturbOpts.fontSizeSigma));
      const dX = rng.gaussian(0, perturbOpts.perturbXSigma);
      const dY = rng.gaussian(0, perturbOpts.perturbYSigma);
      const dTheta = rng.gaussian(0, perturbOpts.perturbThetaSigma);

      // Ink depth variation (affects color darkness)
      const inkDepth = rng.gaussian(0, perturbOpts.inkDepthSigma);
      const alpha = Math.max(0, Math.min(255, 255 - Math.abs(inkDepth) * 3));
      const r = Math.max(0, Math.min(255, inkR + inkDepth));
      const g = Math.max(0, Math.min(255, inkG + inkDepth));
      const b = Math.max(0, Math.min(255, inkB + inkDepth));

      ctx.save();

      // Move to character position with perturbation
      const cx = x + dX;
      const cy = y + dY;

      ctx.translate(cx, cy);
      ctx.rotate(dTheta);

      // Set font and color
      ctx.font = `${fontSize}px "${fontFamily}"`;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha / 255})`;
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';

      // Draw the character
      ctx.fillText(char, 0, 0);

      // Strikethrough
      if (rng.next() < perturbOpts.strikethroughProbability) {
        // const strikeLen = charData.width * (0.5 + rng.next() * 0.8);
        const strikeLen = charData.width * 0.6 + rng.gaussian(0, perturbOpts.strikethroughLengthSigma);
        const strikeAngle = rng.gaussian(0, perturbOpts.strikethroughAngleSigma) * (Math.PI / 180);
        const strikeWidth = Math.max(1, perturbOpts.strikethroughWidth + rng.gaussian(0, perturbOpts.strikethroughWidthSigma));

        ctx.save();
        ctx.rotate(strikeAngle);
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha / 255})`;
        ctx.lineWidth = strikeWidth;
        ctx.beginPath();
        ctx.moveTo(-strikeLen / 2, -fontSize * 0.35);
        ctx.lineTo(strikeLen / 2, -fontSize * 0.35);
        ctx.stroke();
        ctx.restore();
      }

      ctx.restore();
    }
  }

  return canvas;
}

/**
 * Render all pages for a given text.
 *
 * @param {string} text - The text to render
 * @param {object} template - Template options
 * @param {object} [opts] - Additional options
 * @returns {Promise<{pages: HTMLCanvasElement[], totalPages: number}>}
 */
export async function renderAllPages(text, template, opts = {}) {
  // Measure first to get page count
  const measCanvas = document.createElement('canvas');
  const measCtx = measCanvas.getContext('2d');
  const fontFamily = template.fontFamily || 'sans-serif';
  const baseFontSize = template.fontSize || DEFAULT_LAYOUT.fontSize;
  measCtx.font = `${baseFontSize}px "${fontFamily}"`;

  if (fontFamily !== 'sans-serif' && fontFamily !== 'serif') {
    await waitForFont(fontFamily);
  }

  const pages = calculateLayout(text, template, measCtx);
  const totalPages = pages.length;
  const canvases = [];

  for (let i = 0; i < totalPages; i++) {
    const canvas = await renderPage(text, template, i, {
      ...opts,
      seed: opts.seed || Math.floor(Math.random() * 2147483647),
    });
    canvases.push(canvas);
  }

  return { pages: canvases, totalPages };
}

/**
 * Render a preview (first page only, or subset of text).
 *
 * @param {string} text - The text to preview
 * @param {object} template - Template options
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderPreview(text, template) {
  return renderPage(text, template, 0);
}

export default renderPage;
